import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import type { SerializableNode, SerializerProps } from '@/serializer';
import type { ProductData, ProductVariantData } from '@/types';
import { currencyFormatter } from '@/utils/currencyFormatter';
// TODO: make this util generic
import { updateText } from '@/utils/post/utils';

import { getButtonContent } from '../utils/getButtonContent';

import type { CarouselVariant } from '../../imageElement/Image.types';
import { getElement } from '../../serializer/getElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf/index';
import type { Mark } from '../../textLeaf/Text.types';
import type { ProductSerializerTranslations } from './types';

// Strikethrough price styling constants
// Use theme primary color with 50% opacity (fallback to gray if theme not available)
// Using CSS color-mix for modern browsers, with rgba fallback
const getStrikethroughColor = (customColor?: string): string => {
  if (customColor) return customColor;
  // Use CSS variable with color-mix for theme primary at 50% opacity
  // --wt-primary-color is set in Orchid apps (see app/views/web_themes/_stylesheet.css.erb)
  return 'color-mix(in srgb, var(--wt-primary-color, #6B7280) 50%, transparent)';
};

// Calculate strikethrough font size as 3/4 of the current font size
const calculateStrikethroughFontSize = (currentFontSize?: string | number): string => {
  if (!currentFontSize) {
    // Default fallback if no font size is available
    return '12px';
  }

  // Parse the font size (could be "16px", "1rem", "1.5em", etc.)
  const fontSizeStr = String(currentFontSize);
  const match = fontSizeStr.match(/^([\d.]+)([a-z%]+)?$/i);

  if (!match) {
    return '12px'; // Fallback
  }

  const value = Number.parseFloat(match[1]);
  const unit = match[2] || 'px';
  const calculatedValue = value * 0.75; // 3/4 = 0.75

  return `${calculatedValue}${unit}`;
};

interface ExtendedSerializerProps extends SerializerProps {
  product: ProductData;
  productVariant: ProductVariantData;
  imageCarouselVariant?: CarouselVariant;
  node: JSONContent & SerializableNode;
  translations?: ProductSerializerTranslations;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    product,
    productVariant,
    imageCarouselVariant,
    translations,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  const isImage = node?.type === 'image';
  const isCheckoutButton = node?.type === 'button' && node?.attrs?.action === 'product_checkout';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';

  if (isCheckoutButton) {
    if (node?.attrs?.mergeTag === 'product.url') {
      node = {
        ...node,
        attrs: {
          ...node.attrs,
          url: product.url,
        },
      } as JSONContent & SerializableNode;
    }
  }

  // Swap out images
  if (isImage && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'product.image') {
      const variantAssets = productVariant?.assets || [];
      const defaultAsset = variantAssets.find((asset) => asset.is_default);
      const primaryAsset = defaultAsset ?? variantAssets[0];

      const primaryUrl =
        primaryAsset?.url || productVariant?.default_asset_url || product?.default_variant?.default_asset_url;

      const secondaryImages = variantAssets
        .filter((asset) => asset.id !== primaryAsset?.id)
        .map((asset, index) => ({
          url: asset.url,
          alt: asset.alt_text || (product?.name ? `${product.name} - Image ${index + 2}` : `Image ${index + 2}`),
        }));

      node = {
        ...node,
        attrs: {
          ...node.attrs,
          alt: product?.name,
          src: primaryUrl,
          secondaryImages,
          carouselVariant:
            imageCarouselVariant && secondaryImages.length > 0 ? imageCarouselVariant : node.attrs?.carouselVariant,
        },
      } as JSONContent & SerializableNode;
    }
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && node.attrs?.mergeTag) {
    if (node.attrs.mergeTag === 'product.name') {
      node = updateText(node, product?.name) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'product.description') {
      node = updateText(node, product?.description || '') as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'product.cta_text') {
      node = updateText(node, product?.cta_text || '') as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'product.rating') {
      if (product?.reviews_summary) {
        const { average_score, total_reviews_count } = product.reviews_summary;
        const reviewWord =
          total_reviews_count === 1
            ? (translations?.['product_reviews.review'] ?? 'review')
            : (translations?.['product_reviews.reviews'] ?? 'reviews');
        const ratingText = `★ ${average_score.toFixed(1)} (${total_reviews_count.toLocaleString()} ${reviewWord})`;
        node = updateText(node, ratingText) as JSONContent & SerializableNode;
      } else {
        return null;
      }
    }
    if (
      node?.attrs?.mergeTag === 'product.unit_amount' &&
      productVariant?.unit_amount_cents !== undefined &&
      productVariant?.currency
    ) {
      // Display "Free" for free products
      if (product.free) {
        node = updateText(node, translations?.['product.free'] ?? 'Free') as JSONContent & SerializableNode;
      } else {
        const currentPrice = currencyFormatter({
          value: productVariant.unit_amount_cents / 100,
          currency: productVariant.currency,
        });

        // Check if original price exists and is greater than current price
        if (
          productVariant.original_unit_amount_cents &&
          productVariant.original_unit_amount_cents > productVariant.unit_amount_cents
        ) {
          const originalPrice = currencyFormatter({
            value: productVariant.original_unit_amount_cents / 100,
            currency: productVariant.currency,
          });

          // Get the current font size from the original node's content before modifying
          // Check the first text node in the original node's content for fontSize
          const originalTextNode = node.content?.[0];
          const originalTextStyleMark =
            originalTextNode && 'marks' in originalTextNode
              ? (originalTextNode.marks || []).find((m: Mark) => m.type === 'textStyle')
              : null;
          const currentFontSize = originalTextStyleMark?.attrs?.fontSize;

          // Create a node with both prices: current + original (strikethrough)
          const baseNode = updateText(node, `${currentPrice} ${originalPrice}`);
          node = produce(baseNode, (draft) => {
            if (draft.content?.[0]?.type === 'text' && draft.content[0].text) {
              const text = draft.content[0].text;
              const currentPriceLength = currentPrice.length;

              // Split the text into current price and original price
              const currentPriceText = text.substring(0, currentPriceLength);
              const originalPriceText = text.substring(currentPriceLength + 1); // +1 for space

              // Preserve existing marks from the node if any (excluding strike to avoid duplicates)
              const existingMarks = ((draft.content[0].marks || []) as Mark[]).filter((m) => m.type !== 'strike');

              // Get strikethrough styling from paragraph or heading attributes, with defaults
              // Calculate strikethrough font size as 3/4 of current font size
              const strikeAttrs: Record<string, string> = {};
              if ((isParagraph || isHeading) && node.attrs) {
                const nodeAttrs = node.attrs;
                // Use custom color if provided, otherwise use default (theme primary with 50% opacity)
                const customColor = (nodeAttrs.strikethroughColor as string)?.trim();
                strikeAttrs.strikethroughColor = customColor || getStrikethroughColor();
                // Calculate strikethrough font size as 3/4 of current font size
                strikeAttrs.strikethroughFontSize = calculateStrikethroughFontSize(currentFontSize);
                // Use regular font weight for original price
                strikeAttrs.strikethroughFontWeight = 'normal';
              } else {
                // Set defaults if not in paragraph/heading
                strikeAttrs.strikethroughColor = getStrikethroughColor();
                strikeAttrs.strikethroughFontSize = calculateStrikethroughFontSize(currentFontSize);
                strikeAttrs.strikethroughFontWeight = 'normal';
              }

              // Create two text nodes: current price first, then original price with strike mark
              draft.content = [
                {
                  type: 'text',
                  text: `${currentPriceText} `,
                  marks: existingMarks,
                },
                {
                  type: 'text',
                  text: originalPriceText,
                  marks: [
                    ...existingMarks,
                    {
                      type: 'strike',
                      attrs: strikeAttrs,
                    },
                  ],
                },
              ];
            }
          }) as JSONContent & SerializableNode;
        } else {
          // No original price, just show current price
          node = updateText(node, currentPrice) as JSONContent & SerializableNode;
        }
      }
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  if (node?.type === 'button') {
    // This is to prevent hydration errors since this is nested in an anchor tag
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        renderAsDiv: true,
      },
      content: getButtonContent(node, product),
    } as JSONContent & SerializableNode;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          product,
          productVariant,
          imageCarouselVariant,
          translations,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

function ProductSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}

export { ProductSerializer };

export type { ProductSerializerTranslations } from './types';
